import React from "react";
import {
  EAutoExposureMethod,
  PlateTypeCaptureProfile, 
  VisibleCaptureProperty
} from "../../../Model/CaptureProfile";
import EOpticAndCameraFeature from "../../../Model/EOpticAndCameraFeature";
import { LightPathCapability } from "../../../Model/ImagerCapabilities";
import DefaultImagingSettingBinning from "./DefaultImagingSettingBinning";
import DefaultImagingSettingRadioButton from "./DefaultImagingSettingRadioButton";
import DefaultImagingSettingTextBox from "./DefaultImagingSettingTextBox";
import Row from "./Row";
import "./VisibleOpticAndExposure.css";

const AUTO_EXPOSURE_METHOD = "autoExposureMethod";
type Props = {
  lightpathCapabilities: LightPathCapability;
  style?: React.CSSProperties;
  captureProfile: PlateTypeCaptureProfile;
  setCaptureProfile: (captureProfile: PlateTypeCaptureProfile) => void;
  onErrorOccur: (key: string) => void;
  onErrorCleared: (key: string) => void;
};

export default function VisibleOpticAndExposureOptions({
  style = {},
  lightpathCapabilities,
  captureProfile,
  setCaptureProfile,
  onErrorOccur,
  onErrorCleared
}: Props) {
  const visibleCaptureProperty = captureProfile.visible;
  if (visibleCaptureProperty == null) {
    throw new Error("Visible capture property is null");
  }

  const autoExposureAlgorithmProperty = visibleCaptureProperty.autoExposure;
  if (autoExposureAlgorithmProperty == null) {
    throw new Error("Auto exposure algorithm property is null");
  }

  const onValueChanged = (visibleCaptureProperty: VisibleCaptureProperty) => {
    setCaptureProfile({ ...captureProfile, visible: visibleCaptureProperty });
  };

  const gamma = lightpathCapabilities.camera.gamma;
  const gain = lightpathCapabilities.camera.gain;
  const condenser = lightpathCapabilities.optic.condenser;
  const polarizer = lightpathCapabilities.optic.polarizer;
  const brightness = lightpathCapabilities.optic.brightness;
  const binning = lightpathCapabilities.camera.binning;
  const exposure = lightpathCapabilities.camera.exposure;

  let gammaView = null;
  if (gamma != null) {
    if (visibleCaptureProperty.gamma == null) {
      throw new Error("Capture profole gamma is null");
    }
    gammaView = (
      <DefaultImagingSettingTextBox
        value={visibleCaptureProperty.gamma}
        onChange={(name, value) => onValueChanged({ ...visibleCaptureProperty, gamma: value })}
        label="Gamma"
        name={EOpticAndCameraFeature.Gamma}
        unit=""
        min={gamma.minGamma}
        max={gamma.maxGamma}
        disabled={false}
        isInteger={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );
  }

  let gainView = null;
  if (gain != null) {
    if (visibleCaptureProperty.gain == null) {
      throw new Error("Capture profile gain is null");
    }
    gainView = (
      <DefaultImagingSettingTextBox
        value={visibleCaptureProperty.gain}
        onChange={(name, value) => onValueChanged({ ...visibleCaptureProperty, gain: value })}
        label="Gain"
        name={EOpticAndCameraFeature.Gain}
        unit="db"
        min={gain.minGain}
        max={gain.maxGain}
        disabled={false}
        isInteger={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );
  }

  let condenserView = null;
  if (condenser != null) {
    if (visibleCaptureProperty.condenser == null) {
      throw new Error("Capture profile condenser is null");
    }
    condenserView = (
      <DefaultImagingSettingTextBox
        value={visibleCaptureProperty.condenser}
        onChange={(name, value) => onValueChanged({ ...visibleCaptureProperty, condenser: value })}
        label="Condenser"
        name={EOpticAndCameraFeature.Condenser}
        unit="%"
        min={condenser.minCondenser}
        max={condenser.maxCondenser}
        disabled={false}
        isInteger={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );
  }

  let polarizerView = null;
  if (polarizer != null) {
    if (visibleCaptureProperty.polarizer == null) {
      throw new Error("Capture profile polarizer is null");
    }
    polarizerView = (
      <DefaultImagingSettingTextBox
        value={visibleCaptureProperty.polarizer}
        onChange={(name, value) => onValueChanged({ ...visibleCaptureProperty, polarizer: value })}
        label="Polarizer"
        name={EOpticAndCameraFeature.Polarizer}
        unit="°"
        min={polarizer.minPolarizer}
        max={polarizer.maxPolarizer}
        disabled={false}
        isInteger={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );
  }

  let binningView = null;
  if (binning != null) {
    if (visibleCaptureProperty.binning == null) {
      throw new Error("Capture profile binning is null");
    }
    binningView = (
      <DefaultImagingSettingBinning
        binningDisplayValues={binning.binningDisplayValues}
        value={visibleCaptureProperty.binning}
        style={{
          marginLeft: "17px",
          marginBottom: "5px",
        }}
        onChange={(name, value) => onValueChanged({ ...visibleCaptureProperty, binning: value })}
        name="binning"
        disabled={false}
      />
    );
  }

  let minExposureView = null;
  let maxExposureView = null;
  let exposureView = null;

  if (exposure != null) {
    minExposureView = (
      <DefaultImagingSettingTextBox
        value={autoExposureAlgorithmProperty.minExposure}
        onChange={(name, value) => {
          onValueChanged({
            ...visibleCaptureProperty,
            autoExposure: { ...autoExposureAlgorithmProperty, minExposure: value },
          });
        }}
        label="Min Exposure"
        name="minExposure"
        unit="ms"
        min={exposure.minExposure}
        max={autoExposureAlgorithmProperty.maxExposure - 0.1}
        isInteger={false}
        disabled={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );

    maxExposureView = (
      <DefaultImagingSettingTextBox
        value={autoExposureAlgorithmProperty.maxExposure}
        onChange={(name, value) => {
          onValueChanged({
            ...visibleCaptureProperty,
            autoExposure: { ...autoExposureAlgorithmProperty, maxExposure: value },
          });
        }}
        label="Max Exposure"
        name="maxExposure"
        unit="ms"
        min={autoExposureAlgorithmProperty.minExposure + 0.1}
        max={exposure.maxExposure}
        isInteger={false}
        disabled={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );

    if (visibleCaptureProperty.exposure == null) {
      throw new Error("Capture profile exposure is null");
    }

    exposureView = (
      <DefaultImagingSettingTextBox
        value={visibleCaptureProperty.exposure}
        onChange={(name, value) => {
          onValueChanged({
            ...visibleCaptureProperty,
            exposure: value,
          });
        }}
        label="Exposure"
        name="exposure"
        unit="ms"
        min={autoExposureAlgorithmProperty.minExposure}
        max={autoExposureAlgorithmProperty.maxExposure}
        isInteger={false}
        disabled={false}
        style={{
          gridArea: "exposure",
        }}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );
  }

  let brightnessView = null;

  if (brightness != null) {
    if (visibleCaptureProperty.brightness == null) {
      throw new Error("Capture profile brightness is null");
    }

    brightnessView = (
      <DefaultImagingSettingTextBox
        value={visibleCaptureProperty.brightness}
        onChange={(name, value) => {
          onValueChanged({
            ...visibleCaptureProperty,
            brightness: value,
          });
        }}
        label="Brightness"
        name="brightness"
        unit="%"
        min={brightness.minBrightness}
        max={brightness.maxBrightness}
        isInteger={false}
        disabled={false}
        style={{
          gridArea: "brightness",
        }}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );
  }

  const fixedExposureView = (
    <DefaultImagingSettingRadioButton
      name={AUTO_EXPOSURE_METHOD}
      label="Fixed Exposure"
      checked={autoExposureAlgorithmProperty.autoExposureMethod === EAutoExposureMethod.Fixed}
      onChange={(name, value) => {
        onValueChanged({
          ...visibleCaptureProperty,
          autoExposure: { ...autoExposureAlgorithmProperty, autoExposureMethod: EAutoExposureMethod.Fixed },
        });
      }}
      style={{
        marginLeft: "17px",
      }}
      disabled={false}
    />
  );

  const autoExposureDropView = (
    <DefaultImagingSettingRadioButton
      name={AUTO_EXPOSURE_METHOD}
      label="Auto Exposure From First Drop"
      checked={autoExposureAlgorithmProperty.autoExposureMethod === EAutoExposureMethod.Plate}
      onChange={(name, value) => {
        onValueChanged({
          ...visibleCaptureProperty,
          autoExposure: { ...autoExposureAlgorithmProperty, autoExposureMethod: EAutoExposureMethod.Plate },
        });
      }}
      disabled={false}
      style={{
        marginLeft: "17px",
      }}
      
    />
  );

  const autoExposurePlateView = (
    <DefaultImagingSettingRadioButton
      name={AUTO_EXPOSURE_METHOD}
      label="Auto Exposure For Every Drop"
      checked={autoExposureAlgorithmProperty.autoExposureMethod === EAutoExposureMethod.Drop}
      onChange={(name, value) => {
        onValueChanged({
          ...visibleCaptureProperty,
          autoExposure: { ...autoExposureAlgorithmProperty, autoExposureMethod: EAutoExposureMethod.Drop },
        });
      }}
      disabled={false}
      style={{
        marginLeft: "17px",
      }}
    />
  );

  return (
    <div
      className="visible-optic-exposure"
      style={{
        ...style,
      }}
    >
      <span className="title">Optic Options</span>
      {binningView}
      <Row>
        {condenserView}
        {polarizerView}
      </Row>
      <Row>
        {gammaView}
        {gainView}
      </Row>
      <span className="title">Exposure Options</span>
      <Row
        style={{
          backgroundColor: "#E0E0E0",
        }}
      >
        {minExposureView}
        {maxExposureView}
      </Row>
      {fixedExposureView}
      {autoExposureAlgorithmProperty.autoExposureMethod === EAutoExposureMethod.Fixed && (
        <Row
          style={{
            backgroundColor: "#E0E0E0",
          }}
        >
          {exposureView}
          {brightnessView}
        </Row>
      )}
      {autoExposureDropView}
      {autoExposurePlateView}
    </div>
  );
}
